/**
 * Pagina pubblica accettazione invito: `/join/:token`.
 * Step: "choose" (Google / email) → "email" (form completo) | "google_details" (solo campi mancanti dopo Google).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, ROLE_LABELS, setAuth } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle, WarningOctagon, CircleNotch, ShieldCheck, Eye, EyeSlash, GoogleLogo, EnvelopeSimple, ArrowLeft,
} from "@phosphor-icons/react";

const PHONE_RE = /^\+?[0-9 ]{6,20}$/;

// Allineata a _validate_password del backend.
const passwordError = (pw) => {
  if (!pw || pw.length < 8) return "La password deve essere di almeno 8 caratteri.";
  if (!/[A-Z]/.test(pw)) return "La password deve contenere almeno una lettera maiuscola.";
  if (!/[a-z]/.test(pw)) return "La password deve contenere almeno una lettera minuscola.";
  if (!/[0-9]/.test(pw)) return "La password deve contenere almeno un numero.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "La password deve contenere almeno un carattere speciale.";
  return null;
};

const phoneError = (phone) => {
  const p = (phone || "").trim();
  if (!p) return "Numero di cellulare obbligatorio";
  if (!PHONE_RE.test(p)) return "Numero di cellulare non valido";
  return null;
};

const REASON_MESSAGES = {
  invalid: "Il link di invito non è valido o non esiste.",
  expired: "Questo invito è scaduto. Chiedi al tuo referente di generarne uno nuovo.",
  revoked: "Questo invito è stato revocato dall'amministratore.",
};

function PasswordInput({ id, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} required className="pr-10" data-testid={testid} />
      <button
        type="button" onClick={() => setShow((v) => !v)}
        className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-500 hover:text-slate-800"
        aria-label={show ? "Nascondi password" : "Mostra password"} data-testid={`${testid}-toggle`}
      >
        {show ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function Field({ id, label, testid, ...props }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} data-testid={testid} {...props} />
    </div>
  );
}

function InvalidInvite({ reason }) {
  const used = reason === "used";
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="join-invalid">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${used ? "bg-emerald-100" : "bg-red-100"}`}>
          {used
            ? <CheckCircle size={28} weight="fill" className="text-emerald-600" />
            : <WarningOctagon size={28} weight="fill" className="text-red-600" />}
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
          {used ? "Invito già accettato" : "Invito non valido"}
        </h1>
        <p className="text-slate-600 text-sm mb-6" data-testid={`join-error-${reason}`}>
          {used ? "Invito già accettato, accedi normalmente." : (REASON_MESSAGES[reason] || REASON_MESSAGES.invalid)}
        </p>
        <Link
          to={used ? "/login" : "/"}
          className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
          data-testid={used ? "join-go-login" : "join-back-home"}
        >
          {used ? "Vai al login" : "Torna alla home"}
        </Link>
      </div>
    </div>
  );
}

function ChooseStep({ onGoogle, onEmail }) {
  return (
    <div className="space-y-3" data-testid="join-choose">
      <Button type="button" variant="outline" className="w-full h-11" onClick={onGoogle} data-testid="join-choose-google">
        <GoogleLogo size={18} weight="bold" className="mr-2" /> Continua con Google
      </Button>
      <Button type="button" className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white" onClick={onEmail} data-testid="join-choose-email">
        <EnvelopeSimple size={18} weight="bold" className="mr-2" /> Continua con email e password
      </Button>
    </div>
  );
}

function SubmitButton({ submitting }) {
  return (
    <Button type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-2" data-testid="join-submit">
      {submitting
        ? <><CircleNotch className="animate-spin" size={14} /> Creazione account…</>
        : <><CheckCircle weight="fill" size={16} /> Accetta invito e registrati</>}
    </Button>
  );
}

function BackLink({ onBack }) {
  return (
    <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-2" data-testid="join-back-choose">
      <ArrowLeft size={12} /> Cambia metodo
    </button>
  );
}

function EmailForm({ form, set, state, submitting, onSubmit, onBack }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="join-form">
      <BackLink onBack={onBack} />
      <div className="grid grid-cols-2 gap-3">
        <Field id="jf-first" label="Nome" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required autoFocus testid="join-first-name" />
        <Field id="jf-last" label="Cognome" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required testid="join-last-name" />
      </div>
      <div>
        <Field
          id="jf-email" label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
          readOnly={!!state.has_managed_email}
          className={state.has_managed_email ? "bg-slate-100 text-slate-600 cursor-not-allowed" : ""}
          testid="join-email"
        />
        {state.has_managed_email && (
          <p className="text-[11px] text-slate-500 mt-1" data-testid="join-email-managed-hint">Email assegnata dalla tua organizzazione, non modificabile.</p>
        )}
      </div>
      <Field id="jf-phone" label="Numero di cellulare" type="tel" inputMode="tel" placeholder="+39 333 1234567" value={form.phone} onChange={(e) => set("phone", e.target.value)} required testid="join-phone" />
      <Field id="jf-recovery" label="Email di recupero" type="email" placeholder="nome@esempio.it" value={form.recovery_email} onChange={(e) => set("recovery_email", e.target.value)} testid="join-recovery-email" />
      <div>
        <Label htmlFor="jf-pwd">Password</Label>
        <PasswordInput id="jf-pwd" value={form.password} onChange={(e) => set("password", e.target.value)} testid="join-password" />
        <p className="text-[11px] text-slate-500 mt-1">Min 8 caratteri, con maiuscola, minuscola, numero e carattere speciale.</p>
      </div>
      <div>
        <Label htmlFor="jf-pwd2">Conferma password</Label>
        <PasswordInput id="jf-pwd2" value={form.password_confirm} onChange={(e) => set("password_confirm", e.target.value)} testid="join-password-confirm" />
      </div>
      <SubmitButton submitting={submitting} />
    </form>
  );
}

function GoogleDetailsForm({ details, form, set, submitting, onSubmit, onBack }) {
  const missing = details.missing || [];
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="join-google-details-form">
      <BackLink onBack={onBack} />
      <p className="text-sm text-slate-600" data-testid="join-google-email">
        Accesso Google: <strong>{details.google?.email}</strong>. Completa i dati mancanti.
      </p>
      {(missing.includes("first_name") || missing.includes("last_name")) && (
        <div className="grid grid-cols-2 gap-3">
          {missing.includes("first_name") && (
            <Field id="jg-first" label="Nome" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required testid="join-g-first-name" />
          )}
          {missing.includes("last_name") && (
            <Field id="jg-last" label="Cognome" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required testid="join-g-last-name" />
          )}
        </div>
      )}
      <Field id="jg-phone" label="Numero di cellulare" type="tel" inputMode="tel" placeholder="+39 333 1234567" value={form.phone} onChange={(e) => set("phone", e.target.value)} required autoFocus testid="join-g-phone" />
      <Field id="jg-recovery" label="Email di recupero (facoltativa)" type="email" placeholder="nome@esempio.it" value={form.recovery_email} onChange={(e) => set("recovery_email", e.target.value)} testid="join-g-recovery-email" />
      <SubmitButton submitting={submitting} />
    </form>
  );
}

const readGoogleDetails = (token) => {
  try {
    const d = JSON.parse(sessionStorage.getItem("ht_google_invite_details") || "null");
    return d && d.token === token && d.pending_token ? d : null;
  } catch { return null; }
};

export default function JoinInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser, googleLogin } = useAuth();

  const [state, setState] = useState({ loading: true, valid: false });
  const [googleDetails, setGoogleDetails] = useState(() => readGoogleDetails(token));
  const [step, setStep] = useState(() => (readGoogleDetails(token) ? "google_details" : "choose"));
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", recovery_email: "", password: "", password_confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    api.get(`/join/${token}`)
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, ...r.data });
        if (r.data?.has_managed_email && r.data?.invitee_email) set("email", r.data.invitee_email);
      })
      .catch(() => { if (!cancelled) setState({ loading: false, valid: false, reason: "invalid" }); });
    return () => { cancelled = true; };
  }, [token]);

  const startGoogle = () => {
    sessionStorage.setItem("ht_google_invite", JSON.stringify({ token }));
    googleLogin();
  };

  const backToChoose = () => {
    sessionStorage.removeItem("ht_google_invite_details");
    setGoogleDetails(null);
    setStep("choose");
  };

  const finish = async (data) => {
    if (data?.access_token && data?.user) setAuth(data.access_token, data.user, data.refresh_token);
    sessionStorage.removeItem("ht_google_invite_details");
    toast.success(`Benvenuto in ${state.organization_name}!`);
    if (typeof refreshUser === "function") await refreshUser();
    navigate("/dashboard", { replace: true });
  };

  const run = async (request) => {
    setSubmitting(true);
    try {
      const r = await request();
      await finish(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = (e) => {
    e.preventDefault();
    const err = (!form.first_name.trim() || !form.last_name.trim()) ? "Nome e cognome obbligatori"
      : phoneError(form.phone)
      || (form.password !== form.password_confirm ? "Le due password non coincidono" : null)
      || passwordError(form.password);
    if (err) { toast.error(err); return; }
    run(() => api.post(`/join/${token}`, { ...form, phone: form.phone.trim() }));
  };

  const submitGoogle = (e) => {
    e.preventDefault();
    const missing = googleDetails?.missing || [];
    const err = (missing.includes("first_name") && !form.first_name.trim()) || (missing.includes("last_name") && !form.last_name.trim())
      ? "Nome e cognome obbligatori" : phoneError(form.phone);
    if (err) { toast.error(err); return; }
    run(() => api.post(`/join/${token}/google-complete`, {
      pending_token: googleDetails.pending_token,
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      phone: form.phone.trim(),
      recovery_email: form.recovery_email.trim() || null,
    }));
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="join-loading">
        <CircleNotch className="animate-spin text-purple-600" size={36} />
      </div>
    );
  }
  if (!state.valid) return <InvalidInvite reason={state.reason || "invalid"} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-slate-50 p-6" data-testid="join-page">
      <div className="max-w-md w-full">
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src="/logo.png" alt="HouseTag" className="w-10 h-10" />
          <span className="font-display text-2xl font-bold text-slate-900">Housetag<span className="text-purple-600">.it</span></span>
        </div>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck size={28} weight="fill" className="text-purple-600" />
            </div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mb-1" data-testid="join-org-title">
              Sei stato invitato a unirti a <span className="text-purple-600">{state.organization_name}</span>
            </h1>
            <p className="text-sm text-slate-500">
              Ruolo: <span className="font-medium text-slate-700" data-testid="join-role-label">
                {ROLE_LABELS[state.role] || state.role}{state.is_supervisor ? " (supervisore)" : ""}
              </span>
            </p>
          </div>

          {step === "choose" && <ChooseStep onGoogle={startGoogle} onEmail={() => setStep("email")} />}
          {step === "email" && (
            <EmailForm form={form} set={set} state={state} submitting={submitting} onSubmit={submitEmail} onBack={backToChoose} />
          )}
          {step === "google_details" && googleDetails && (
            <GoogleDetailsForm details={googleDetails} form={form} set={set} submitting={submitting} onSubmit={submitGoogle} onBack={backToChoose} />
          )}

          <p className="text-[11px] text-slate-400 text-center mt-4">
            Registrandoti accetti i Termini di Servizio e la Privacy Policy di Housetag.
          </p>
        </div>
      </div>
    </div>
  );
}
